import React, { useState, useRef } from 'react';
import { Upload, X, Loader } from 'lucide-react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { Button } from './Button';

interface ImageUploadProps {
    label?: string;
    value?: string;
    onChange: (url: string, storageId?: string) => void;
    className?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
    label = 'Upload Image',
    value,
    onChange,
    className = ''
}) => {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const generateUploadUrl = useMutation(api.storage.generateUploadUrl);

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError('Please upload an image file (JPG, PNG, WebP)');
            return;
        }

        // Validate file size (5MB)
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size must be less than 5MB');
            return;
        }

        setError(null);
        setUploading(true);
        setProgress(0);

        try {
            // Get upload URL from Convex
            const uploadUrl = await generateUploadUrl();

            // Simulate progress (Convex doesn't provide upload progress)
            const progressInterval = setInterval(() => {
                setProgress(prev => Math.min(prev + 10, 90));
            }, 100);

            // Upload file to Convex storage
            const response = await fetch(uploadUrl, {
                method: 'POST',
                headers: { 'Content-Type': file.type },
                body: file,
            });

            clearInterval(progressInterval);

            if (!response.ok) {
                throw new Error('Failed to upload image');
            }

            const { storageId } = await response.json();

            // Get the URL for the uploaded file
            // The URL will be resolved by the parent component using useQuery
            setProgress(100);
            onChange(storageId, storageId);
            setUploading(false);
            setProgress(0);
        } catch (err) {
            console.error('Error uploading:', err);
            setError('Failed to upload image. Please try again.');
            setUploading(false);
            setProgress(0);
        }
    };

    const handleRemove = () => {
        onChange('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className={`space-y-2 ${className}`}>
            {label && (
                <label className="block text-sm font-medium text-white/70">
                    {label}
                </label>
            )}

            <div className="relative">
                {value ? (
                    <div className="relative rounded-xl overflow-hidden border border-white/10 group">
                        <img
                            src={value}
                            alt="Uploaded preview"
                            className="w-full h-48 object-cover"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <Button
                                variant="danger"
                                size="sm"
                                onClick={handleRemove}
                            >
                                <X className="w-4 h-4 mr-2" />
                                Remove Image
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className={`
                            border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all
                            ${uploading
                                ? 'border-[#FF991C]/30 bg-[#FF991C]/5'
                                : 'border-white/10 hover:border-[#FF991C]/40 hover:bg-white/5'
                            }
                        `}
                    >
                        {uploading ? (
                            <div className="space-y-3">
                                <Loader className="w-8 h-8 text-[#FF991C] animate-spin mx-auto" />
                                <div className="text-sm text-[#FF991C] font-medium">
                                    Uploading... {Math.round(progress)}%
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-1.5 overflow-hidden">
                                    <div
                                        className="bg-[#FF991C] h-full transition-all duration-300"
                                        style={{ width: `${progress}%` }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-3">
                                    <Upload className="w-6 h-6 text-white/30" />
                                </div>
                                <p className="text-sm font-medium text-white">
                                    Click to upload image
                                </p>
                                <p className="text-xs text-white/40">
                                    JPG, PNG, WebP up to 5MB
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                />
            </div>

            {error && (
                <p className="text-sm text-[#EF4444] flex items-center gap-1">
                    <X className="w-3 h-3" /> {error}
                </p>
            )}
        </div>
    );
};
